import WarpElement from '@warp-ds/elements-core';
import { css } from 'lit';
import { html } from 'lit/static-html.js';
import pulse from '@finn-no/pulse-sdk';
import './market-item.js';
import '@warp-ds/components/icon';

export class MarketPlaceNavigationBar extends WarpElement {
    static styles = [
        ...WarpElement.styles,
        css`
            *,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}.auto-cols-\[minmax\(10\.8rem\,_2fr\)\]{grid-auto-columns:minmax(10.8rem,2fr)}.grid-rows-2{grid-template-rows:repeat(2,minmax(0,1fr))}.overflow-x-auto{overflow-x:auto}.static{position:static}.hover\:s-text-subtle:hover{color:var(--w-s-color-text-subtle)}.ml-6{margin-left:.6rem}.mr-16{margin-right:1.6rem}.py-20{padding-top:2rem;padding-bottom:2rem}@media (width>=768px){.md\:pb-0{padding-bottom:0}.md\:pt-16{padding-top:1.6rem}}@media (width>=990px){.lg\:overflow-hidden{overflow:hidden}};
        `,
    ];

    static properties = {
        markets: { type: Array, attribute: 'markets', reflect: true },
        brand: { type: String, attribute: 'brand', reflect: true },
        exploreMore: { type: Object, attribute: 'explore-more', reflect: true },
        locale: { type: String, attribute: 'locale', reflect: true },
    };

    connectedCallback() {
        super.connectedCallback();

        const proxy = document.querySelector('[data-skip-link-proxy]');
        if (proxy) {
            proxy.addEventListener('focus', () => {
                const firstMarketItem = this.shadowRoot?.querySelector('market-item');
                
                if (firstMarketItem) {
                    const firstLink = firstMarketItem.shadowRoot?.querySelector('#first-market-link');
                    
                    if (firstLink) {
                        firstLink.focus({ preventScroll: true });

                        requestAnimationFrame(() => {
                            const rect = firstLink.getBoundingClientRect();
                            const offset = 55;
                            window.scrollTo({
                                top: window.scrollY + rect.top - offset,
                                behavior: 'smooth',
                            });
                        });
                    }
                }
            });
        }
    }

    trackMarketLinkClick(trackingData) {
        pulse.trackEvent({
            type: 'Click',
            intent: 'Open',
            name: trackingData.name,
            object: {
                id: trackingData.object.id,
                type: 'UIElement',
                elementType: trackingData.object.elementType,
                position: trackingData.object.position,
            },
            target: trackingData.target,
            vertical: {
                '@type': trackingData.vertical.type,
                name: trackingData.vertical.name,
            },
        });
    }

    // Explore more button, under the MPNB, that links to the category explorer.
    _exploreMoreButton() {
        const { url, tracking, label } = this.exploreMore;

        return html`<a
            class="flex justify-end hover:s-text-subtle"
            href=${url}
            data-explore-more-button
            @click=${() => this.trackMarketLinkClick(tracking)}
        >
            ${label}<span class="sr-only">,&nbsp;</span>
            <w-icon name="ChevronRight" size="small" locale="${this.locale}" class="ml-6 self-center"></w-icon>
        </a>`;
    }

    /**
     * Get markets with desktop limit applied (max 16 items on desktop)
     */
    get limitedMarkets() {
        if (!this.markets?.length) return [];
        
        const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 990;
        const maxDesktopItems = 16;
        
        if (isDesktop && this.markets.length > maxDesktopItems) {
            return this.markets.slice(0, maxDesktopItems);
        }
        
        return this.markets;
    }

    render() {
        if (this.markets?.length > 0) {
            const markets = this.limitedMarkets;

            return html`
                <!-- Custom container with overflow-x visible, for a scrollable div that extends outside margins. -->
                <nav aria-labelledby="market-grid-title" data-markets-pulse-wrapper class="py-20 md:pt-16 md:pb-0">
                    <h2 class="sr-only" id="market-grid-title">
                        ${this.i18n.t({
                            id: 'title.frontpage.markets',
                            message: 'Markets',
                        })}
                    </h2>
                    <div>
                        <div class="relative">
                            <div class="overflow-x-auto lg:overflow-hidden">
                                <!-- Gradient (fadeout) overlay div. -->
                                <div class="px-8 grid grid-flow-col grid-rows-2 auto-cols-[minmax(10.8rem,_2fr)]">
                                    <!-- MARKET ICONS -->
                                    ${markets.map(
                                        (market, index) =>
                                            html`<market-item
                                                .market=${market}
                                                .index=${index}
                                                .locale=${this.locale}
                                                brand=${this.brand}
                                            ></market-item>`,
                                    )}
                                    <!-- /MARKET ICONS -->
                                </div>
                            </div>
                        </div>
                    </div>
                    ${this.exploreMore ? html`<div class="flex justify-end py-8 mr-16">${this._exploreMoreButton()}</div>` : ''}
                </nav>
            `;
        }
        return html`<div></div>`;
    }
}

if (!customElements.get('marketplace-navigation-bar')) {
    customElements.define('marketplace-navigation-bar', MarketPlaceNavigationBar);
}
